import React, { useEffect, useState } from 'react';
import { message } from '../Message';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { CircularProgress, Chip, Box } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';

const PhotoCarts = ({ t, firebase }) => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [imageLoading, setImageLoading] = useState({});

  const columns = [
    { id: 'actions', label: 'Actions', minWidth: 100 },
    { id: 'status', label: 'Status', minWidth: 120 },
    { id: 'paymentId', label: 'Payment ID', minWidth: 120 },
    { id: 'price', label: 'Price', minWidth: 100 },
    { id: 'quantity', label: 'Quantity', minWidth: 100 },
    { id: 'photoPackIds', label: 'Photo Pack', minWidth: 150 },
    { id: 'createdAt', label: 'Created At', minWidth: 170 },
  ];

  useEffect(() => {
    fetchPhotoOrders();
  }, []);

  const fetchPhotoOrders = async () => {
    try {
      setLoading(true);
      const result = await firebase.getPhotoCarts();
      const orders = result.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const formattedOrders = orders.map(order => ({
        ...order,
        createdAt: formatDate(order.createdAt),
        status: (
          <Chip
            icon={order.status === 'completed' ? <CheckCircleIcon /> : <PendingIcon />}
            label={order.status}
            color={order.status === 'pending' ? 'warning' : 'success'}
            variant="outlined"
          />
        ),
        actions: (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => handleViewDetails(order)}
          >
            View
          </Button>
        ),
        photoPackIds: Array.isArray(order.photoPackIds) 
          ? order.photoPackIds.join(', ') 
          : order.photoPackIds,
        price: typeof order.price === 'string' ? order.price : `$${order.price}`
      }));

      setRows(formattedOrders);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching photo orders:', error);
      message.error('Failed to load photo orders');
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (typeof timestamp === 'string') {
      return new Date(timestamp).toLocaleString();
    }
    return timestamp.seconds 
      ? new Date(timestamp.seconds * 1000).toLocaleString()
      : 'N/A';
  };

  const handleViewDetails = async (order) => {
    // Format the order data before setting it
    const formattedOrder = {
      ...order,
      // Customer Details
      age: order.age || 'N/A',
      email: order.email || await getUserEmail(order.uid) || 'N/A',
      gender: order.gender || 'N/A',
      ethnicity: order.ethnicity || 'N/A',
      eyesColor: order.eyesColor || 'N/A',
      hairColor: order.hairColor || 'N/A',
      // Order Details
      price: typeof order.price === 'string' ? order.price : `$${order.price}`,
      quantity: order.quantity || 'N/A',
      status: order.status || 'N/A',
      photoPackIds: Array.isArray(order.photoPackIds) 
        ? order.photoPackIds.join(', ') 
        : (order.photoPackIds || 'N/A'),
      createdAt: formatDate(order.createdAt)
    };
  
    setSelectedOrder(formattedOrder);
    setDetailsDialogOpen(true);
  };

  const getUserEmail = async (uid) => {
    if (!uid) return null;
    try {
      const userDoc = await firebase.getUser(uid);
      return userDoc?.data()?.email;
    } catch (error) {
      console.error('Error fetching user email:', error);
      return null;
    }
  };

  const handleImageLoad = (index) => {
    setImageLoading(prev => ({
      ...prev,
      [index]: false
    }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" padding={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="my-design ">
      <h2>Headshot Orders</h2>
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    style={{ minWidth: column.minWidth, fontWeight: 'bold' }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <TableRow hover key={row.id}>
                    {columns.map((column) => (
                      <TableCell key={column.id}>
                        {row[column.id]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Order Details</DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <DialogContentText component="div">
              <Box mb={2}>
                <strong>Order ID:</strong> {selectedOrder.id}
              </Box>
              <Box mb={2}>
                <strong>Payment ID:</strong> {selectedOrder.paymentId}
              </Box>
              <Box mb={2}>
                <strong>Customer Details:</strong>
                <ul>
                  <li>ID: {selectedOrder.uid || 'Empty'}</li>
                  <li>Email: {selectedOrder.email || 'Empty'}</li>
                  <li>Age: {selectedOrder.age || 'Empty'}</li>
                  <li>Gender: {selectedOrder.gender || 'Empty'}</li>
                  <li>Ethnicity: {selectedOrder.ethnicity || 'Empty'}</li>
                  <li>Eyes Color: {selectedOrder.eyesColor || 'Empty'}</li>
                  <li>Hair Color: {selectedOrder.hairColor || 'Empty'}</li>
                </ul>
              </Box>
              <Box mb={2}>
                <strong>Order Details:</strong>
                <ul>
                  <li>Price: {selectedOrder.price || 'Empty'}</li>
                  <li>Quantity: {selectedOrder.quantity || 'Empty'}</li>
                  <li>Status: {selectedOrder.status || 'Empty'}</li>
                  <li>Photo Pack: {selectedOrder.photoPackIds || 'Empty'}</li>
                  <li>Created At: {selectedOrder.createdAt || 'Empty'}</li>
                </ul>
              </Box>
              <Box mb={2}>
                <strong>Images:</strong>
                <ImageList sx={{ width: '100%' }} cols={3} gap={8}>
                  {selectedOrder.images && selectedOrder.images.map((imageUrl, index) => (
                    <ImageListItem key={index} sx={{ height: '300px !important' }}>
                      {imageLoading[index] && (
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          height="100%"
                          width="100%"
                        >
                          <CircularProgress />
                        </Box>
                      )}
                      <img
                        src={imageUrl}
                        alt={`Order image ${index + 1}`}
                        loading="lazy"
                        onLoad={() => handleImageLoad(index)}
                        style={{
                          display: imageLoading[index] ? 'none' : 'block',
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              </Box>
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PhotoCarts;